/**
 * Schema Form Fields Component
 * Reusable form fields for editing JSON output schemas
 * Used by both AgentConfig SchemaEditor and System Settings OutputSchemasPanel
 */

import React, { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/Button';
import {
  AlertCircle,
  CheckCircle,
  Sparkles,
  Loader2,
  ChevronDown,
  Key,
} from 'lucide-react';
import { outputSchemasAdminApi } from '@/lib/api/outputSchemas';
import { useChatModelsQuery } from '@/lib/queries/useModelsQuery';
import { useI18n } from '@/i18n';

// ============================================================================
// Types
// ============================================================================

export interface SchemaFormData {
  key: string;
  name: string;
  description?: string | null;
  json_schema: Record<string, unknown>;
  example_output?: Record<string, unknown> | null;
  format_instruction: string;
}

export interface SchemaFormFieldsProps {
  // Form data
  data: SchemaFormData;
  onChange: (updates: Partial<SchemaFormData>) => void;

  // Options
  isKeyEditable?: boolean;
  showKeyField?: boolean;

  // Validation errors (optional, for external validation)
  jsonSchemaError?: string | null;
  exampleError?: string | null;
  onJsonSchemaErrorChange?: (error: string | null) => void;
  onExampleErrorChange?: (error: string | null) => void;
}

// ============================================================================
// Key validation
// ============================================================================

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
const normalizeKey = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, '');
};

// ============================================================================
// Component
// ============================================================================

export const SchemaFormFields: React.FC<SchemaFormFieldsProps> = ({
  data,
  onChange,
  isKeyEditable = true,
  showKeyField = true,
  jsonSchemaError: externalJsonError,
  exampleError: externalExampleError,
  onJsonSchemaErrorChange,
  onExampleErrorChange,
}) => {
  const { messages, language } = useI18n();
  const t = messages.agentConfig?.schemaEditor;

  // Internal validation state (used if external not provided)
  const [internalJsonError, setInternalJsonError] = useState<string | null>(null);
  const [internalExampleError, setInternalExampleError] = useState<string | null>(null);

  const jsonSchemaError = externalJsonError ?? internalJsonError;
  const exampleError = externalExampleError ?? internalExampleError;
  const setJsonSchemaError = onJsonSchemaErrorChange ?? setInternalJsonError;
  const setExampleError = onExampleErrorChange ?? setInternalExampleError;

  // AI Generation state
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [aiExplanation, setAiExplanation] = useState('');

  // Queries and mutations
  const { data: chatModels = [] } = useChatModelsQuery();
  const generateMutation = useMutation({
    mutationFn: outputSchemasAdminApi.generate,
  });

  // Format JSON for editor
  const jsonSchemaString = JSON.stringify(data.json_schema || {}, null, 2);
  const exampleOutputString = data.example_output
    ? JSON.stringify(data.example_output, null, 2)
    : '';

  // Handlers
  const handleJsonSchemaChange = useCallback(
    (value: string | undefined) => {
      if (!value) return;
      try {
        const parsed = JSON.parse(value);
        onChange({ json_schema: parsed });
        setJsonSchemaError(null);
      } catch (e) {
        setJsonSchemaError((e as Error).message);
      }
    },
    [onChange, setJsonSchemaError]
  );

  const handleExampleOutputChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        onChange({ example_output: null });
        setExampleError(null);
        return;
      }
      try {
        const parsed = JSON.parse(value);
        onChange({ example_output: parsed });
        setExampleError(null);
      } catch (e) {
        setExampleError((e as Error).message);
      }
    },
    [onChange, setExampleError]
  );

  const handleAiGenerate = async () => {
    // Use example_output as input for AI generation
    if (!data.example_output) {
      alert(t?.alertExampleRequired || (language === 'zh' ? '请先填写示例输出' : 'Please provide example output first'));
      return;
    }
    if (!selectedModel) {
      alert(t?.alertModelRequired || (language === 'zh' ? '请选择模型' : 'Please select a model first'));
      return;
    }

    try {
      const exampleJson = JSON.stringify(data.example_output, null, 2);
      const result = await generateMutation.mutateAsync({
        example_json: exampleJson,
        model: selectedModel,
        language: language === 'zh' ? 'zh' : 'en',
      });

      // Fill form with generated data (keep example_output as-is since user already provided it)
      onChange({
        json_schema: result.json_schema,
        format_instruction: result.format_instruction,
        name: data.name || result.suggested_name,
        key: data.key || result.suggested_key,
      });

      setAiExplanation(result.explanation);
    } catch (err) {
      console.error('AI generation failed:', err);
      alert(
        (t?.alertGenerateFailed || (language === 'zh' ? 'AI 生成失败: ' : 'AI generation failed: ')) +
          (err instanceof Error ? err.message : 'Unknown error')
      );
    }
  };

  const isValidKey = KEY_PATTERN.test(data.key);

  return (
    <div className="space-y-4">
      {/* Example Output Editor - at the TOP */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t?.exampleOutput || (language === 'zh' ? '示例输出' : 'Example Output')} <span className="text-red-500">*</span>
          </label>
          {exampleError ? (
            <span className="flex items-center gap-1 text-xs text-red-500">
              <AlertCircle className="w-3 h-3" />
              {exampleError}
            </span>
          ) : data.example_output ? (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              {t?.validJson || (language === 'zh' ? 'JSON 有效' : 'Valid JSON')}
            </span>
          ) : null}
        </div>
        <div className="h-[150px] border border-gray-300 rounded-lg overflow-hidden">
          <Editor
            height="100%"
            defaultLanguage="json"
            value={exampleOutputString}
            onChange={handleExampleOutputChange}
            options={{
              minimap: { enabled: false },
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              fontSize: 13,
              tabSize: 2,
              wordWrap: 'on',
            }}
            theme="vs-light"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t?.exampleOutputHint ||
            (language === 'zh'
              ? '输入期望的 JSON 格式示例，AI 将据此生成 Schema'
              : 'Input an example of the expected JSON format, AI will generate Schema based on it')}
        </p>
      </div>

      {/* AI Generation Panel - simplified, only model selector */}
      <div className="border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
        <button
          type="button"
          onClick={() => setShowAiPanel(!showAiPanel)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left"
        >
          <Sparkles className="w-5 h-5 text-purple-500" />
          <span className="flex-1 font-medium text-purple-800">
            {t?.aiGenerate || (language === 'zh' ? 'AI 自动生成 Schema' : 'AI Auto-Generate Schema')}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-purple-500 transition-transform ${
              showAiPanel ? 'rotate-180' : ''
            }`}
          />
        </button>

        {showAiPanel && (
          <div className="px-4 pb-4 space-y-3">
            <p className="text-sm text-purple-700">
              {t?.aiGenerateDesc ||
                (language === 'zh'
                  ? '基于上方的示例输出，AI 将自动分析结构并生成对应的 JSON Schema'
                  : 'Based on the example output above, AI will analyze the structure and generate the corresponding JSON Schema')}
            </p>

            {/* Model Selector + Generate Button in one row */}
            <div className="flex items-center gap-3">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
              >
                <option value="">
                  {t?.selectModelPlaceholder || (language === 'zh' ? '选择模型...' : 'Select a model...')}
                </option>
                {chatModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={handleAiGenerate}
                disabled={generateMutation.isPending || !data.example_output || !selectedModel}
                className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t?.generating || (language === 'zh' ? '生成中...' : 'Generating...')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t?.generate || (language === 'zh' ? '生成 Schema' : 'Generate Schema')}
                  </>
                )}
              </Button>
            </div>

            {/* AI Explanation */}
            {aiExplanation && (
              <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-purple-500">✨</span>
                  <div>
                    <p className="text-xs font-medium text-purple-800 mb-1">
                      {t?.aiExplanation || (language === 'zh' ? 'AI 分析说明' : 'AI Explanation')}
                    </p>
                    <p className="text-sm text-gray-700">{aiExplanation}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Key Field */}
      {showKeyField && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Key className="w-4 h-4 inline-block mr-1" />
            {t?.key || 'Schema Key'} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.key}
            onChange={(e) => onChange({ key: normalizeKey(e.target.value) })}
            disabled={!isKeyEditable}
            className={`w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              isValidKey || !data.key ? 'border-gray-300' : 'border-red-300 bg-red-50'
            }`}
            placeholder="e.g., lucky_charm, simple_response"
          />
          <p className={`text-xs mt-1 ${isValidKey || !data.key ? 'text-gray-500' : 'text-red-500'}`}>
            {isValidKey || !data.key
              ? (t?.keyValidHint || (language === 'zh' ? '小写字母、数字、下划线，必须以字母开头' : 'Lowercase letters, numbers, underscores. Must start with a letter.'))
              : (t?.keyInvalidHint || (language === 'zh' ? '格式无效' : 'Invalid format'))}
          </p>
        </div>
      )}

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.name || (language === 'zh' ? '名称' : 'Name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t?.namePlaceholder || (language === 'zh' ? '输入名称' : 'Enter name')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.description || (language === 'zh' ? '描述' : 'Description')}
          </label>
          <input
            type="text"
            value={data.description || ''}
            onChange={(e) => onChange({ description: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t?.descriptionPlaceholder || (language === 'zh' ? '输入描述' : 'Enter description')}
          />
        </div>
      </div>

      {/* JSON Schema Editor */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t?.jsonSchema || 'JSON Schema'} <span className="text-red-500">*</span>
          </label>
          {jsonSchemaError ? (
            <span className="flex items-center gap-1 text-xs text-red-500">
              <AlertCircle className="w-3 h-3" />
              {jsonSchemaError}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              {t?.validJson || (language === 'zh' ? 'JSON 有效' : 'Valid JSON')}
            </span>
          )}
        </div>
        <div className="h-[180px] border border-gray-300 rounded-lg overflow-hidden">
          <Editor
            height="100%"
            defaultLanguage="json"
            value={jsonSchemaString}
            onChange={handleJsonSchemaChange}
            options={{
              minimap: { enabled: false },
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              fontSize: 13,
              tabSize: 2,
              wordWrap: 'on',
            }}
            theme="vs-light"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t?.jsonSchemaHint ||
            (language === 'zh'
              ? '定义输出的 JSON 结构，使用 JSON Schema draft-07 格式'
              : 'Define the JSON structure using JSON Schema draft-07 format')}
        </p>
      </div>

      {/* Format Instruction */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t?.formatInstruction || (language === 'zh' ? '格式说明' : 'Format Instruction')}
        </label>
        <textarea
          value={data.format_instruction}
          onChange={(e) => onChange({ format_instruction: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          placeholder="请严格按以下 JSON 格式输出：\n```json\n{schema}\n```"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t?.formatInstructionHint ||
            (language === 'zh'
              ? '使用 {schema} 作为占位符，运行时会替换为实际的 JSON Schema'
              : 'Use {schema} as a placeholder, it will be replaced with the actual JSON Schema at runtime')}
        </p>
      </div>
    </div>
  );
};

export default SchemaFormFields;
